import { useQuery } from "@apollo/client"
import { Link, useParams } from "react-router-dom"
import { gql } from "~/__generated__"
import { useViewerMaybe } from "~/auth/use-viewer"
import { GraphqlError } from "~/ui/errors"
import Text from "~/ui/typography"
import { lettermemePath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { ScreenLoading } from "~/ui/screen-loading"
import { LettermemeFooter } from "./lettermeme-footer"
import { NewsletterView } from "./newsletter-view"
import { useTrackingCode } from "~/common/use-tracking-code"

const lettermemeWeekInReviewQuery = gql(/* GraphQL */ `
  query LettermemeWeekInReview($urlPath: String!) {
    lettermeme(urlPath: $urlPath) {
      ...LettermemeView
      latestWeekInReview {
        __typename
        ...NewsletterView
      }
    }
  }
`)

const viewerOwnedLettermemesQuery = gql(/* GraphQL */ `
  query ViewerOwnedLettermemesForLettermemeWeekInReviewScreen {
    viewer {
      id
      ownedLettermemes {
        id
      }
    }
  }
`)

export const LettermemeWeekInReviewScreen: React.FC = () => {
  const { urlPath } = useParams<{ urlPath: string }>()
  const { viewer } = useViewerMaybe()
  const { data, error, loading } = useQuery(lettermemeWeekInReviewQuery, {
    variables: { urlPath: urlPath! },
    skip: !urlPath,
  })
  const { data: ownedData } = useQuery(viewerOwnedLettermemesQuery, {
    fetchPolicy: "cache-first",
    skip: !viewer,
  })

  useDocumentTitle(data?.lettermeme?.name || "Lettermeme")
  useTrackingCode(data?.lettermeme?.trackingCode)
  if (error) return <GraphqlError error={error} />
  if (loading) return <ScreenLoading />

  if (!data?.lettermeme) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <Text as="h1" className="mb-4 text-2xl font-bold">
          Lettermeme not found
        </Text>
        <Text>The lettermeme you're looking for doesn't exist.</Text>
      </div>
    )
  }

  const lettermeme = data.lettermeme
  const newsletter = lettermeme.latestWeekInReview
  const isOwner = Boolean(
    viewer &&
      lettermeme.id &&
      ownedData?.viewer?.ownedLettermemes?.some(
        (ownedLettermeme) => ownedLettermeme.id === lettermeme.id
      )
  )
  const isPaidLocked =
    lettermeme.monetizationEnabled && lettermeme.viewerIsSubscribed !== true && !isOwner
  const isFreeLocked = !viewer && !lettermeme.monetizationEnabled
  const isPreview = isPaidLocked || isFreeLocked

  if (!newsletter || newsletter.__typename !== "NewsletterSummary") {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <Text as="h1" className="mb-4 text-2xl font-bold">
          Week in Review
        </Text>
        <Text className="text-gray-500">
          No weekly summary yet. Check back after the first full week of daily lettermemes.
        </Text>
        <Link
          to={lettermemePath({ urlPath: lettermeme.urlPath })}
          className="mt-4 inline-block text-gray-400 underline"
        >
          View the full archive
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl p-8">
      <NewsletterView
        lettermeme={lettermeme}
        newsletter={newsletter}
        variant={isPreview ? "preview" : "full"}
      />
      <LettermemeFooter />
    </div>
  )
}
